/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { OrganizationRole } from './organization';

/**
 * Verifies if the required role is covered by the assigned roles.
 */
export const hasProjectPermission = (
  organizationRoles: OrganizationRole[],
  projectRoles: ProjectRole[],
  requiredPermission: ProjectPermission,
): boolean => {
  // Handle implicit roles
  if (organizationRoles.includes('owner')) {
    projectRoles.push('owner');
  }
  /**
   * Grants implicit read access to all projects
   * for users assigned the organization read-only role
   */
  if (organizationRoles.includes('read-only')) {
    projectRoles.push('read-only');
  }
  return projectRoles.some(role => ProjectRolePermissions[role][requiredPermission]);
};

export const extractRoles = (
  organizations: {
    id: string;
    roles: OrganizationRole[];
    projects: { id: string; roles: ProjectRole[] }[];
  }[],
  organizationId: string,
  projectId: string,
) => {
  const organization = organizations.find(organization => organization.id === organizationId);

  const organizationRoles = organization?.roles || [];
  const projectRoles = organization?.projects.find(project => project.id === projectId)?.roles || [];

  return { organizationRoles, projectRoles };
};

export const ProjectRoles = ['owner', 'cluster-manager', 'read-only'] as const;
export type ProjectRole = (typeof ProjectRoles)[number];

/**
 * All these permissions are within an organization project
 */
export const ProjectPermissions = [
  /**
   * Read the project
   */
  'read_project',
  /**
   * Administrating a project:
   *  - changing the name and description
   *  - changing the project thumbnail
   *  - adding and removing users or teams to the project
   *  - changing the roles of users or teams in the project
   */
  'edit_project',
  /**
   * Delete the project
   */
  'delete_project',

  /**
   * Read clusters within the project
   */
  'read_cluster',
  /**
   * Create a new cluster within the project (includes cloning existing clusters)
   */
  'create_cluster',
  /**
   * Administrating a cluster:
   *   - changing the name, description or project
   *   - creating, aborting and deleting logical backups
   *   - starting and stopping the cluster
   *   - restarting a cluster node
   *   - resetting the password
   *   - moving a cluster into another project (requires the `create_cluster` permission in the target project)
   */
  'edit_cluster',
  /**
   * Delete a cluster from the project
   */
  'delete_cluster',
] as const;
export type ProjectPermission = (typeof ProjectPermissions)[number];

const ProjectRolePermissions: Record<ProjectRole, Record<ProjectPermission, boolean>> = {
  owner: {
    create_cluster: true,
    delete_cluster: true,
    delete_project: true,
    edit_cluster: true,
    edit_project: true,
    read_cluster: true,
    read_project: true,
  },
  'cluster-manager': {
    create_cluster: true,
    delete_cluster: true,
    delete_project: false,
    edit_cluster: true,
    edit_project: false,
    read_cluster: true,
    read_project: true,
  },
  'read-only': {
    create_cluster: false,
    delete_cluster: false,
    delete_project: false,
    edit_cluster: false,
    edit_project: false,
    read_cluster: true,
    read_project: true,
  },
};
